import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';

export default function NotificationBell({ collapsed = false }) {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const loadCount = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get('/notifications/unread-count').catch(() => ({ data: { unread: 0 } })),
        api.get('/chat/unread-count').catch(() => ({ data: { unread: 0 } })),
      ]);
      setCount((r1.data?.unread || 0) + (r2.data?.unread || 0));
    } catch (_) { /* ignore */ }
  };
  const loadList = async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications', { params: { limit: 30 } });
      setItems(r.data?.notifications || []);
    } catch (_) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, 30000); // poll 30 s
    return () => clearInterval(id);
  }, []);

  const togglePanel = async () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const handleClick = async (n) => {
    try {
      if (!n.read) {
        await api.post(`/notifications/${n.notification_id}/read`);
      }
    } catch (_) {}
    setOpen(false);
    loadCount();
    if (n.task_id) navigate(`/taches/${n.task_id}`);
    else if (n.channel_id) navigate(`/discussions?channelId=${n.channel_id}`);
  };

  const markAll = async () => {
    try {
      await api.post('/notifications/read-all');
      setCount(0);
      loadList();
    } catch (_) {}
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={togglePanel}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notif-bell-btn"
        title="Notifications"
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {count > 0 && (
          <span
            key="notif-count-badge"
            className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
            data-testid="notif-count"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-80 max-h-[70vh] bg-white rounded-lg shadow-2xl border border-slate-200 z-40 flex flex-col"
            data-testid="notif-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Notifications</h3>
              <button
                type="button"
                onClick={markAll}
                disabled={count === 0}
                className="text-xs text-blue-600 hover:text-blue-800 disabled:text-slate-300 flex items-center gap-1"
                data-testid="notif-mark-all-read"
              >
                <CheckCheck className="w-3 h-3" /> Tout lire
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="p-6 text-center text-slate-500" key="notif-loading">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              )}
              {!loading && items.length === 0 && (
                <p key="notif-empty" className="p-6 text-center text-sm text-slate-500">Aucune notification.</p>
              )}
              {!loading && items.map((n) => (
                <button
                  key={n.notification_id}
                  type="button"
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${
                    n.read ? '' : 'bg-blue-50/50'
                  }`}
                  data-testid={`notif-item-${n.notification_id}`}
                >
                  <p className="text-sm text-slate-900 font-medium leading-snug">{n.title}</p>
                  {n.message && <p className="text-xs text-slate-600 mt-0.5">{n.message}</p>}
                  <p className="text-[10px] text-slate-400 mt-1">
                    {String(n.created_at || '').slice(0, 16).replace('T', ' ')}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
